/**
 * Demo Contacts - Wikipedia "Alice and Bob" characters + Mock Companies
 *
 * Standard cryptographic protocol placeholders used for demo/testnet scenarios.
 * These contacts are only shown when demo mode is enabled or on testnet/signet/regtest.
 *
 * Also includes mock companies for demonstrating B2B payment scheduling flows.
 *
 * @see https://en.wikipedia.org/wiki/Alice_and_Bob
 * @see docs/DemoGuide.md#p2p-contacts-demo-testnet-only
 */

export interface DemoContact {
  id: string;
  name: string;
  role: string;
  description: string;
  /** Demo RGB address (not real - for display only) */
  rgbAddress?: string;
  /** Demo Lightning pubkey (not real - for display only) */
  lnPubkey?: string;
}

/**
 * Canonical demo contacts from Wikipedia "Alice and Bob" article
 * Used consistently across SATCHEL demo scenarios
 */
export const DEMO_CONTACTS: DemoContact[] = [
  {
    id: "alice",
    name: "Alice",
    role: "Honest Party",
    description: "Primary message sender/initiator in demos",
    rgbAddress: "rgb:2WBcas9-yjzEvGuf-oJ8GydSx-oJ8GydSx-demo",
    lnPubkey: "03alice...demo",
  },
  {
    id: "bob",
    name: "Bob",
    role: "Counterparty",
    description: "Primary message receiver/responder in demos",
    rgbAddress: "rgb:3XCdbt0-zkAfwHvg-pK9HzeTs-pK9HzeTs-demo",
    lnPubkey: "03bob...demo",
  },
  {
    id: "carol",
    name: "Carol",
    role: "Third Party",
    description: "Third participant in multi-party protocols",
    rgbAddress: "rgb:4YDeum1-alBgxIwh-qL0IafUt-qL0IafUt-demo",
  },
  {
    id: "dave",
    name: "Dave",
    role: "Fourth Party",
    description: "Fourth participant in multi-party protocols",
    rgbAddress: "rgb:5ZEfvn2-bmChyJxi-rM1JbgVu-rM1JbgVu-demo",
  },
  {
    id: "eve",
    name: "Eve",
    role: "Eavesdropper",
    description: "Passive attacker who listens to communications",
  },
  {
    id: "mallory",
    name: "Mallory",
    role: "Active Attacker",
    description: "Malicious attacker who modifies/injects messages",
  },
  {
    id: "trent",
    name: "Trent",
    role: "Trusted Third Party",
    description: "Neutral arbiter trusted by all participants",
  },
  {
    id: "peggy",
    name: "Peggy",
    role: "Prover",
    description: "Party proving knowledge in zero-knowledge proofs",
  },
  {
    id: "victor",
    name: "Victor",
    role: "Verifier",
    description: "Party verifying proofs in zero-knowledge protocols",
  },
  {
    id: "walter",
    name: "Walter",
    role: "Warden",
    description: "Guard or custodian in custody protocols",
  },
  {
    id: "wendy",
    name: "Wendy",
    role: "Warden",
    description: "Secondary warden in multi-custodian setups",
  },
  {
    id: "judy",
    name: "Judy",
    role: "Judge",
    description: "Adjudicator in dispute resolution protocols",
  },
  {
    id: "oscar",
    name: "Oscar",
    role: "Opponent/Adversary",
    description: "Generic adversary in security analysis",
  },
  {
    id: "chuck",
    name: "Chuck",
    role: "Charlie Alias",
    description: "Alternative third party (alias for Carol)",
  },
  // Mock Companies - for B2B payment scheduling demos
  {
    id: "acme-corp",
    name: "Acme Corp",
    role: "Vendor",
    description: "Mock supplier for recurring B2B payments",
    rgbAddress: "rgb:7BGhwo4-cnDizKyj-tN2KchWv-tN2KchWv-demo",
  },
  {
    id: "globex-inc",
    name: "Globex Inc",
    role: "Service Provider",
    description: "Mock service provider for subscription payments",
    rgbAddress: "rgb:8CHixp5-doEjaLzk-uO3LdiXw-uO3LdiXw-demo",
  },
  {
    id: "initech-llc",
    name: "Initech LLC",
    role: "Contractor",
    description: "Mock contractor for milestone-based payments",
    rgbAddress: "rgb:9DIjyq6-epFkbMal-vP4MejYx-vP4MejYx-demo",
  },
  {
    id: "umbrella-dao",
    name: "Umbrella DAO",
    role: "DAO Treasury",
    description: "Mock DAO for governance token distributions",
    rgbAddress: "rgb:0EJkzr7-fqGlcNbm-wQ5NfkZy-wQ5NfkZy-demo",
  },
];

/**
 * Get demo contacts, optionally filtered by role type
 */
export function getDemoContacts(roleFilter?: string): DemoContact[] {
  if (!roleFilter) return DEMO_CONTACTS;
  return DEMO_CONTACTS.filter((c) => c.role.toLowerCase().includes(roleFilter.toLowerCase()));
}

/**
 * Get a specific demo contact by ID
 */
export function getDemoContact(id: string): DemoContact | undefined {
  return DEMO_CONTACTS.find((c) => c.id === id);
}
